// src/components/AddPaymentModal.js

import React, { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Box, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Typography, RadioGroup, FormControlLabel, Radio, Divider, Alert, Chip
} from '@mui/material';

const formatCurrency = (amount) => (amount || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });

const EMPTY_ARRAY = [];

const AddPaymentModal = ({ isOpen, onClose, onSave, kalanTutar = 0, customerName, openRecords = EMPTY_ARRAY }) => {
  const [tutar, setTutar] = useState('');
  const [odemeYontemi, setOdemeYontemi] = useState('Nakit');
  const [aciklama, setAciklama] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState('genel');

  useEffect(() => {
    if (isOpen) {
      // Modal açıldığında seçimi sıfırla
      setSelectedRecordId(openRecords.length > 0 ? '' : 'genel');
      setTutar('');
      setOdemeYontemi('Nakit');
      setAciklama('');
    }
  }, [isOpen, openRecords.length]);

  // Seçilen kayıt değiştiğinde kalan tutarı otomatik doldur
  useEffect(() => {
    if (selectedRecordId && selectedRecordId !== 'genel') {
      const record = openRecords.find(r => r.id === selectedRecordId);
      if (record) {
        setTutar(record.kalanTutar.toString());
      }
    } else if (selectedRecordId === 'genel' && kalanTutar > 0) {
      setTutar(kalanTutar.toString());
    }
  }, [selectedRecordId, openRecords, kalanTutar]);

  const handleSubmit = async () => {
    if (!tutar || parseFloat(tutar) <= 0) {
      alert('Lütfen geçerli bir tutar giriniz.');
      return;
    }

    // Açık kayıt varsa seçim zorunlu
    if (openRecords.length > 0 && !selectedRecordId) {
      alert('Lütfen tahsilat yapılacak işlemi seçiniz.');
      return;
    }

    // Seçilen kayda göre uyarı (artık engellemek yerine onay soruyoruz)
    const maxTutar = selectedRecordId && selectedRecordId !== 'genel'
      ? (openRecords.find(r => r.id === selectedRecordId)?.kalanTutar || 0)
      : kalanTutar;

    if (maxTutar > 0 && parseFloat(tutar) > maxTutar) {
      if (!window.confirm(`Girdiğiniz tutar (${formatCurrency(parseFloat(tutar))}) kalan borçtan (${formatCurrency(maxTutar)}) fazla. Bu durumda bakiye eksiye düşecektir (biz borçlu olacağız). Devam etmek istiyor musunuz?`)) {
        return;
      }
    }

    setIsSaving(true);
    try {
      const selectedRecord = selectedRecordId && selectedRecordId !== 'genel'
        ? openRecords.find(r => r.id === selectedRecordId)
        : null;

      await onSave({
        tutar: parseFloat(tutar),
        odemeYontemi: odemeYontemi,
        aciklama: aciklama || `Ödeme (${odemeYontemi})`,
        selectedRecord: selectedRecord
      });
      onClose();
    } catch (error) {
      console.error("Ödeme kaydedilirken hata: ", error);
      alert("Bir hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const title = customerName ? `Tahsilat Ekle: ${customerName}` : 'Ödeme Ekle';

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Kayıt Seçimi */}
          {openRecords.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Bu tahsilat hangi işlem için?
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                Tahsilatı doğru işleme bağlamak borç takibini sağlıklı tutar.
              </Alert>
              <RadioGroup
                value={selectedRecordId}
                onChange={(e) => setSelectedRecordId(e.target.value)}
              >
                {openRecords.map(record => (
                  <FormControlLabel
                    key={record.id}
                    value={record.id}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {record.label}
                        </Typography>
                        {record.description && (
                          <Typography variant="caption" color="text.secondary">
                            — {record.description}
                          </Typography>
                        )}
                        <Chip
                          label={`Kalan: ${formatCurrency(record.kalanTutar)}`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Box>
                    }
                    sx={{
                      mb: 0.5,
                      border: '1px solid',
                      borderColor: selectedRecordId === record.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mx: 0,
                      px: 1,
                      transition: 'border-color 0.2s'
                    }}
                  />
                ))}
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  value="genel"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Genel Tahsilat (belirli bir kayda bağlanmadan)
                    </Typography>
                  }
                  sx={{ mx: 0, px: 1 }}
                />
              </RadioGroup>
            </Box>
          )}

          <TextField label="Tutar (₺)" type="number" value={tutar} onChange={(e) => setTutar(e.target.value)} required autoFocus />
          <FormControl fullWidth>
            <InputLabel>Ödeme Yöntemi</InputLabel>
            <Select value={odemeYontemi} label="Ödeme Yöntemi" onChange={(e) => setOdemeYontemi(e.target.value)}>
              <MenuItem value="Nakit">Nakit</MenuItem>
              <MenuItem value="Kredi Kartı">Kredi Kartı</MenuItem>
              <MenuItem value="Havale/EFT">Havale/EFT</MenuItem>
              <MenuItem value="Cari Hesaptan / Daha Önce Alındı">Cari Hesaptan / Daha Önce Alındı (Düzeltme)</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Açıklama (Opsiyonel)" value={aciklama} onChange={(e) => setAciklama(e.target.value)} multiline rows={2} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} disabled={isSaving}>İptal</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaymentModal;